/* ==========================================================================
   FAVOURITES — a heart on every product card, and one in the header.

   ── WHAT IT IS, STATED PLAINLY BECAUSE THE ANSWER IS A TRADE ──────────────
   BROWSER-LOCAL. One `localStorage` key, this device, this browser. Nothing is
   sent anywhere: no request, no cookie, no customer record. That is the whole
   of it, and the consequences are real rather than theoretical:

     clears their browser     the list is gone, permanently
     a second device          a different list; they do not merge
     a private window         empty, and discarded when the window closes
     signs in                 changes nothing — see below

   WHY LOCAL RATHER THAN ACCOUNT-TIED. Shopify has no native wishlist, so an
   account-backed list needs somewhere to put per-customer data: a paid app, the
   Customer Account API, or a Worker of our own keyed on customer id. All three
   are real work, and this store has 0 customers and 0 orders — measured
   2026-09-05 — so an account-backed list today would synchronise nothing for
   nobody. Local works for every visitor, signed in or not, from the first tap.

   RUGGABLE'S IS THE SAME SHAPE. Their heart goes to /pages/favorite-rugs, that
   page reads localStorage, and their account dropdown carries a separate "My
   Favorites" for signed-in users. Local first, account-backed once there is an
   account worth backing it with.

   THE UPGRADE PATH IS DELIBERATELY OPEN. `list()` returns handles, so a future
   sync writes exactly that array to wherever a customer record lives and reads
   it back on load. Nothing here has to be unpicked; a merge step is added in
   front of `read()`. Do not key this on a numeric product id — a handle is what
   the Rug Finder's cards carry and what a favourites page can fetch with.

   ── ACCOUNTS ARE UNTOUCHED ───────────────────────────────────────────────
   The account icon is still in the header, and /account still works. The heart
   is an ADDITION, not a replacement: somebody who has ordered reaches their
   orders exactly as they did before. That was a stated condition and it is met
   by not removing anything.

   ── THE KEY IS THE HANDLE, AND IT IS READ RATHER THAN BUILT ──────────────
   Every card carries a real link to the product, so the handle is taken out of
   `href`. It is never assembled from a name or a SKU: CLAUDE.md records a URL
   built by string formatting serving manufacturer RPBR009 "Thicket" the AI
   design Thicket's photograph, at HTTP 200, because 30 names are shared between
   the two sources. A parsed href cannot make that mistake.
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'rz.favourites.v1';
  var EVENT = 'rugzella:favourites';
  var MAX = 200;

  /* EVERY READ AND WRITE IS GUARDED. localStorage throws outright in some
     contexts — a private window with site data blocked, a browser configured to
     refuse it, a thumbnail capture — and it can come back empty for reasons
     that are not a bug. A heart that throws would take the whole card renderer
     down with it, so the failure mode is "no favourites", never "no page". */
  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      var v = raw ? JSON.parse(raw) : [];
      return Array.isArray(v) ? v.filter(function (h) { return typeof h === 'string' && h; }) : [];
    } catch (e) { return []; }
  }

  function write(list) {
    try { window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); }
    catch (e) { /* full, blocked, or refused — the in-page state still updates */ }
  }

  var state = read();

  function announce() {
    try { window.dispatchEvent(new CustomEvent(EVENT, { detail: { list: state.slice() } })); }
    catch (e) {}
  }

  var API = {
    list: function () { return state.slice(); },
    count: function () { return state.length; },
    has: function (h) { return state.indexOf(h) !== -1; },
    add: function (h) {
      if (!h || API.has(h)) return;
      state.unshift(h);              // newest first, which is what a list page wants
      write(state); announce();
    },
    remove: function (h) {
      var i = state.indexOf(h);
      if (i === -1) return;
      state.splice(i, 1); write(state); announce();
    },
    toggle: function (h) { API.has(h) ? API.remove(h) : API.add(h); return API.has(h); }
  };
  window.RugWishlist = API;

  /* ── the heart itself ──────────────────────────────────────────────────
     TWO PATHS, ONE STROKE-WIDTH, so the outline and the filled state are the
     same shape at the same weight and the control does not appear to move when
     it is tapped. `currentColor` on both, so one CSS rule drives the state. */
  function heartSvg() {
    return '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
           '<path d="M12 20.4 3.9 12.6a4.9 4.9 0 0 1 0-7 5 5 0 0 1 7 0l1.1 1 1.1-1a5 5 0 0 1 7 0 4.9 4.9 0 0 1 0 7Z"/>' +
           '</svg>';
  }

  function handleFrom(el) {
    var a = el.querySelector('a[href*="/products/"]');
    if (!a) return '';
    var m = (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  function titleFrom(el, handle) {
    var a = el.querySelector('a[href*="/products/"]');
    var t = (a && (a.getAttribute('aria-label') || a.textContent || '')).trim();
    return t || handle;
  }

  function paint(btn, on) {
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.classList.toggle('is-saved', !!on);
    /* THE ACCESSIBLE NAME SAYS WHAT THE PRESS WILL DO, and it names the rug.
       "Save" alone on a grid of twenty cards is twenty identical controls to a
       screen-reader user moving by form control. */
    var name = btn.getAttribute('data-title') || 'this rug';
    btn.setAttribute('aria-label', (on ? 'Remove ' : 'Save ') + name + (on ? ' from favourites' : ' to favourites'));
  }

  function mount(card, target, forced) {
    var host = target || card;
    if (!card || !host || host.querySelector(':scope > .rz-heart')) return;
    /* THE HANDLE IS PASSED IN WHERE THERE IS NO CARD TO READ IT FROM. On the
       product page the only link to this product is the address bar, so
       `mountProduct()` parses it there -- the same regex, against a string the
       browser gave us rather than one we assembled. */
    var handle = (forced && forced.handle) || handleFrom(card);
    if (!handle) return;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'rz-heart';
    btn.setAttribute('data-handle', handle);
    btn.setAttribute('data-title', (forced && forced.title) || titleFrom(card, handle));
    btn.innerHTML = heartSvg();
    paint(btn, API.has(handle));

    /* ── THE HEART ALSO OPENED THE ZOOM, AND stopPropagation COULD NOT STOP IT
       (2026-09-06) ────────────────────────────────────────────────────────
       Pressing the heart on a gallery image saved the rug AND opened the
       full-screen zoom. The listener below already called `stopPropagation`,
       so the obvious reading was that it was not firing. It was firing, and
       it was too late.

       `assets/component.js` registers ONE delegated listener per event type,
       ON `document`, WITH `{ capture: true }`. Capture runs from the document
       DOWN to the target, so it is the FIRST handler to see the click - before
       anything bound on the heart, which is at the bottom of the tree. By the
       time this function runs, that handler has already called
       `target.closest('[on\\:click]')`, resolved it to the enclosing
       `<slideshow-slide class="product-media-container" on:click="#zoom-dialog-…/open/N">`
       and opened the dialog. NO bubble-phase listener anywhere can prevent
       that, and neither can a capture listener on the heart itself.

       SO THE FIX IS TO STOP THE RESOLUTION, NOT THE PROPAGATION. `getElement`
       returns `target.closest('[on:click]')` - the NEAREST ancestor carrying
       the attribute - and then `if (!method) return;`. Giving the heart an
       EMPTY `on:click` makes the heart itself that nearest ancestor, and an
       empty value splits to no method, so Horizon's own handler returns having
       done nothing and never reaches the slide. The framework's own lookup is
       what refuses the zoom.

       THIS BEATS THE ALTERNATIVES ON ROBUSTNESS, WHICH IS WHY IT IS THE ONE
       CHOSEN. Registering our own capture listener on `document` would depend
       on script ORDER - same target, same phase, first registered wins - and
       `component.js` is a module imported by the theme's own bundle. Moving
       the heart out of the media container would put it outside its positioned
       ancestor. Forking the gallery to move `on:click` onto an inner element
       would fork a file we already fork once too often.

       `on:pointerdown` GETS THE SAME TREATMENT for the same reason: the
       slideshow binds drag on pointerdown, and a press that starts on the
       heart should not start dragging the carousel. Both are in
       `component.js`'s delegated list. */
    btn.setAttribute('on:click', '');
    btn.setAttribute('on:pointerdown', '');

    btn.addEventListener('click', function (ev) {
      /* THE CARD IS A LINK, AND ON HORIZON IT IS A FULL-BLEED OVERLAY ANCHOR.
         Without both of these a tap on the heart navigates to the product page
         instead of saving it. `stopPropagation` is not enough on its own —
         Horizon also binds a view-transition handler on the card element, and
         on the product page it is not enough at all (see above). */
      ev.preventDefault();
      ev.stopPropagation();
      paint(btn, API.toggle(handle));
    });

    host.appendChild(btn);
  }

  /* THE HEART GOES ON THE IMAGE, NOT ON THE CARD, because "top-right of the
     card" and "top-right of the photograph" are different places the moment a
     card has a title under it. The gallery is the positioned ancestor. */
  function imageHost(card) {
    return card.querySelector('.card-gallery') ||
           card.querySelector('.product-media-container') ||
           card.querySelector('.fit-card__thumb-wrap') ||
           card;
  }

  /* `product-card` is Horizon's; `.fit-card` is the Rug Finder's own markup,
     built in rug-finder.js — different element, same contract: an <a> to
     /products/<handle> and an <img> of the rug. `.rz-bcard-wrap` is the
     homepage rail, whose card IS its anchor, so its heart lives in a wrapper
     beside it — rug-home-bestsellers.liquid explains why. */
  var CARDS = 'product-card, .fit-card, .rz-bcard-wrap';

  function mountOne(c) {
    mount(c, c.matches('.rz-bcard-wrap') ? c : imageHost(c));
  }

  /* ── THE PRODUCT PAGE ──────────────────────────────────────────────────
     The same heart, in the same place, on the main product image.

     THE HANDLE COMES OUT OF THE ADDRESS BAR, WHICH IS THE SAME DISCIPLINE THE
     CARDS FOLLOW. A card reads it from an href the theme printed; here there is
     no such link, so it is read from the URL the browser is already on. Either
     way it is PARSED from a real address rather than assembled from a title or
     a SKU -- the `RPBR009 "Thicket"` scar, where a constructed URL returned
     HTTP 200 with the wrong rug's photograph.

     THERE ARE THREE COPIES OF EVERY IMAGE IN HORIZON'S GALLERY AND ONLY TWO OF
     THEM ARE THE PRODUCT. Measured on the live page: 33 `.product-media-container`
     for an 11-image product -- 11 inside `slideshow-slides` (the mobile
     carousel), 11 inside `ul.media-gallery__grid` (the desktop grid) and 11
     inside `ul.dialog-zoomed-gallery` (the zoom overlay). Both visible tracks
     get a heart, because which one is on screen is a media query rather than
     something this file can know. The ZOOM COPY DELIBERATELY DOES NOT: a
     favourites button floating over a full-screen zoom is a control in the way
     of the thing it is covering, and it is the same rug already hearted twice. */
  function productHandle() {
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  function mountProduct(root) {
    var handle = productHandle();
    if (!handle) return;
    var scope = root && root.querySelectorAll ? root : document;
    var galleries = [];
    if (scope.nodeType === 1 && scope.matches && scope.matches('media-gallery')) galleries.push(scope);
    scope.querySelectorAll('media-gallery').forEach(function (g) { galleries.push(g); });
    if (!galleries.length) return;

    var h1 = document.querySelector('.product-information h1, h1');
    var title = (h1 && h1.textContent || '').trim() || handle;

    galleries.forEach(function (g) {
      ['slideshow-slides', '.media-gallery__grid'].forEach(function (sel) {
        var track = g.querySelector(sel);
        if (!track) return;
        /* EVERY IMAGE, NOT THE FIRST ONE (2026-09-05). It was
           `querySelector` - one heart per track, on the hero - and somebody
           looking at the third photograph had no way to save the rug from
           where they were standing. The heart is a per-PRODUCT control that
           happens to be drawn on a per-IMAGE surface, so `mount` is idempotent
           and every copy reads and writes the same stored handle: pressing any
           one of them fills all of them, because they are one favourite. */
        track.querySelectorAll('.product-media-container').forEach(function (m) {
          mount(m, m, { handle: handle, title: title });
        });
      });
    });
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    /* THE ROOT ITSELF, AND NOT ONLY ITS DESCENDANTS. `querySelectorAll` never
       returns the node it is called on, and the observer below hands this the
       ADDED NODE — which on the surface that matters most IS the card. The Rug
       Finder appends each `<article class="fit-card">` straight into its results
       container, so every recommendation was being skipped. Measured on the live
       page with a synthetic card of the same shape: 0 hearts mounted before this
       line existed, 1 after. Collections and search hid it, because those cards
       arrive inside a container and were found as descendants. */
    if (scope.nodeType === 1 && scope.matches && scope.matches(CARDS)) mountOne(scope);
    scope.querySelectorAll(CARDS).forEach(mountOne);
    /* HORIZON RE-INSERTS THE WHOLE PRODUCT-INFORMATION SECTION ON EVERY VARIANT
       CHANGE -- CLAUDE.md records that as the reason the visualiser loads its
       challenge script lazily rather than from a <script> tag in the block. So
       picking a different size destroys the gallery and the heart with it, and
       this has to run on every added subtree rather than once at load. */
    mountProduct(scope);
  }

  /* ── the header count ──────────────────────────────────────────────────── */
  function paintHeader() {
    var n = API.count();
    document.querySelectorAll('[data-rz-fav-count]').forEach(function (el) {
      el.textContent = n ? String(n) : '';
      el.hidden = !n;
    });
    document.querySelectorAll('[data-rz-fav-link]').forEach(function (el) {
      el.setAttribute('aria-label', n === 1 ? 'Favourites, 1 rug' : 'Favourites, ' + n + ' rugs');
    });
  }

  /* Every heart for the same rug repaints together — the same product can be on
     screen twice (a grid and a recommendation rail), and two hearts disagreeing
     about one rug is the kind of thing a person notices immediately. */
  window.addEventListener(EVENT, function () {
    document.querySelectorAll('.rz-heart').forEach(function (b) {
      paint(b, API.has(b.getAttribute('data-handle')));
    });
    paintHeader();
  });

  /* A SECOND TAB IS A REAL CASE. localStorage fires `storage` in the OTHER tab,
     so saving in one updates the rest without a reload. */
  window.addEventListener('storage', function (e) {
    if (e.key !== KEY) return;
    state = read();
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { list: state.slice() } }));
  });

  function start() {
    scan(document);
    paintHeader();
    /* Cards arrive after load on every surface that matters: the Rug Finder
       renders its six on a response, collections paginate, the cart drawer
       injects recommendations. A one-shot scan would cover the first screen
       and silently miss all of it. */
    if (window.MutationObserver) {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            if (added[j].nodeType === 1) scan(added[j]);
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else { start(); }
})();
